// Shared visual helpers for the Travela blog design system.
// Blogs have no banner image in the CMS yet, so cards/heroes fall back to a
// generated tinted cover with a big serif "mark" derived from the title.

export interface IBlogTint {
  tint: string; // cover background
  ink: string; // mark + eyebrow color
}

const BLOG_TINTS: [IBlogTint, ...IBlogTint[]] = [
  { tint: "#FFE6EF", ink: "#B62A61" },
  { tint: "#F1ECFA", ink: "#6B4E9E" },
  { tint: "#FDEDE1", ink: "#B4693A" },
  { tint: "#E8F1EE", ink: "#3F7A69" },
  { tint: "#ECEFF8", ink: "#47598F" },
];

/** Diagonal hatch texture overlaid on every generated cover. */
export const BLOG_HATCH =
  "repeating-linear-gradient(115deg, rgba(255,255,255,0) 0 9px, rgba(255,255,255,0.5) 9px 10px)";

// Stable string hash so a given blog keeps the same tint across home, list,
// related and detail surfaces (not tied to list position).
function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function tintFor(seed: string): IBlogTint {
  const list = BLOG_TINTS;
  return list[hashString(seed || "travela") % list.length] ?? list[0];
}

const MARK_STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "to",
  "for",
  "of",
  "in",
  "on",
  "and",
  "with",
  "your",
  "you",
  "how",
  "why",
  "where",
  "what",
  "when",
  "top",
  "best",
  "finest",
  "guide",
  "is",
  "are",
  "at",
  "from",
  "practical",
  "complete",
]);

/**
 * Derive a short serif "mark" (1–2 significant words) from the title,
 * used as the decorative cover label when there is no banner image.
 */
export function deriveMark(title: string): string {
  if (!title) return "Travela";
  // Strip punctuation (keeps letters of any script — Latin, Bangla — and digits)
  // without relying on \p{…} unicode escapes, which need an es6+ compile target.
  const words = title
    .replace(/["'“”‘’.,:;!?()[\]{}<>—–\-_/\\|#*~`…]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const significant = words.filter(
    (w) => !MARK_STOP_WORDS.has(w.toLowerCase()) && !/^\d+$/.test(w),
  );
  const source = significant.length ? significant : words;
  let mark = source.slice(0, 2).join(" ");
  if (mark.length > 16) mark = source[0] || "Travela";
  return mark || "Travela";
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Deterministic "Jul 27, 2026" formatting (UTC, so SSR == client). */
export function formatBlogDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${MONTHS[d.getUTCMonth()]} ${day}, ${d.getUTCFullYear()}`;
}

/** Estimate reading time from CMS HTML at ~200 wpm ("N min read"). */
export function readingTime(html: string | null | undefined): string {
  const text = (html || "").replace(/<[^>]*>/g, " ").replace(/&[^;]+;/g, " ");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
}
